import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useSession } from '../App.jsx';
import { inr, num, qty } from '../lib/format.js';
import { parseNumber } from '../lib/numbers.js';
import { Alert, Card, Field } from '../components/ui.jsx';
import ItemPicker from '../components/ItemPicker.jsx';

const blankLine = () => ({ item_id: '', qty: '', rate: '' });
/** For the running totals only: an entry that is not yet a number counts as nothing. */
const figure = (v) => { const n = parseNumber(v); return Number.isFinite(n) ? n : 0; };

/** Document 1 — the only screen where the goods are described. */
export default function NewDeliveryOrder() {
  const session = useSession();
  const navigate = useNavigate();
  const entityId = session.entities[0] && session.entities[0].id;

  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [form, setForm] = useState({ party_id: '', godown_id: '', place_of_supply: '', remarks: '', vehicle_no: '', transporter: '' });
  const [lines, setLines] = useState([blankLine()]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // item_id -> { rate, min_rate }, for one buyer at a time.
  const [priced, setPriced] = useState({ partyId: null, prices: {} });
  const [credit, setCredit] = useState(null);
  const [buyerSearch, setBuyerSearch] = useState('');
  const [chosen, setChosen] = useState(null);

  useEffect(() => {
    if (!entityId) return;
    api.get('/api/godowns').then((g) => {
      setGodowns(g.godowns);
      setForm((f) => ({ ...f, godown_id: g.godowns[0] ? g.godowns[0].id : '' }));
    }).catch((e) => setError(e.message));
  }, [entityId]);

  // Only what can be sold now, and all of it — no page limit. Fetched again
  // when the godown changes, so each item also shows what that godown holds.
  useEffect(() => {
    if (!entityId) return undefined;
    let live = true;
    const godown = form.godown_id ? `&godown_id=${form.godown_id}` : '';
    api.get(`/api/items?entity_id=${entityId}&in_stock=true${godown}`)
      .then((i) => live && setItems(i.items))
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [entityId, form.godown_id]);
  const godownName = (godowns.find((g) => g.id === form.godown_id) || {}).name;

  // The buyer list is searched on the server: a ledger beyond the first page
  // must still be reachable by name or GSTIN.
  useEffect(() => {
    if (!entityId) return undefined;
    let live = true;
    const term = buyerSearch.trim();
    const t = setTimeout(() => {
      api.get(`/api/parties?entity_id=${entityId}${term ? `&search=${encodeURIComponent(term)}` : ''}`)
        .then((p) => live && setParties(p.parties))
        .catch((e) => live && setError(e.message));
    }, term ? 250 : 0);
    return () => { live = false; clearTimeout(t); };
  }, [entityId, buyerSearch]);

  const party = (chosen && chosen.id === form.party_id) ? chosen : parties.find((p) => p.id === form.party_id);
  const buyerOptions = chosen && !parties.some((p) => p.id === chosen.id) ? [chosen, ...parties] : parties;

  // The buyer's own rates and their credit position, fetched as soon as they are
  // chosen — the counter should see the floor before quoting, not after.
  // Each effect drops its answer if the buyer changed while it was in flight,
  // so one buyer's figures never land on another's order.
  useEffect(() => {
    setCredit(null);
    if (!form.party_id || !entityId) return undefined;
    let live = true;
    api.get(`/api/credit-check?party_id=${form.party_id}&order_value=0`)
      .then((c) => live && setCredit(c)).catch(() => {});
    return () => { live = false; };
  }, [form.party_id, entityId]);

  // The server prices one item per call, so only the items on the order are
  // asked about (not the whole catalogue), and each only once per buyer.
  const lineItems = [...new Set(lines.map((l) => l.item_id).filter(Boolean))].sort().join(',');
  const prices = priced.partyId === form.party_id ? priced.prices : {};
  useEffect(() => {
    if (!form.party_id || !entityId) { setPriced({ partyId: null, prices: {} }); return undefined; }
    const partyId = form.party_id;
    const known = priced.partyId === partyId ? priced.prices : {};
    const missing = lineItems ? lineItems.split(',').filter((id) => !(id in known)) : [];
    if (priced.partyId !== partyId) setPriced({ partyId, prices: {} });
    if (!missing.length) return undefined;
    let live = true;
    Promise.all(missing.map((id) =>
      api.get(`/api/price-check?entity_id=${entityId}&party_id=${partyId}&item_id=${id}`)
        .then((p) => [id, p]).catch(() => [id, null])))
      .then((pairs) => {
        if (!live) return;
        setPriced((prev) => ({
          partyId,
          prices: { ...(prev.partyId === partyId ? prev.prices : {}), ...Object.fromEntries(pairs.filter(([, p]) => p)) },
        }));
      });
    return () => { live = false; };
  }, [form.party_id, entityId, lineItems]); // eslint-disable-line react-hooks/exhaustive-deps
  const entity = session.entities[0];
  const interstate = party && entity
    && String(form.place_of_supply || '').padStart(2, '0') !== String(entity.state_code || '').padStart(2, '0');

  const preview = useMemo(() => lines.reduce((acc, l) => {
    const item = items.find((i) => i.id === l.item_id);
    if (!item || !(figure(l.qty) > 0)) return acc;
    const taxable = figure(l.qty) * figure(l.rate);
    const gst = (taxable * num(item.gst_rate)) / 100;
    return { taxable: acc.taxable + taxable, gst: acc.gst + gst };
  }, { taxable: 0, gst: 0 }), [lines, items]);

  const setLine = (idx, patch) => setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const belowFloor = (line) => {
    const price = prices[line.item_id];
    const rate = parseNumber(line.rate);
    return Boolean(price && price.min_rate !== null && Number.isFinite(rate) && rate < num(price.min_rate));
  };

  const submit = async (issue) => {
    setBusy(true); setError(null);
    try {
      // A line nobody touched is skipped; a line with something unreadable in
      // it is refused rather than quietly left off the order.
      const out = [];
      lines.forEach((l, idx) => {
        const qtyN = parseNumber(l.qty);
        const rateN = parseNumber(l.rate);
        if (!l.item_id && qtyN === null) return;
        const where = `Line ${idx + 1}`;
        if (!l.item_id) throw new Error(`${where}: choose the item`);
        if (!Number.isFinite(qtyN) || qtyN <= 0) throw new Error(`${where}: the quantity must be a number above zero`);
        if (Number.isNaN(rateN) || (rateN !== null && rateN < 0)) throw new Error(`${where}: the rate must be a number, or blank for the list rate`);
        out.push({ item_id: l.item_id, qty: qtyN, ...(rateN === null ? {} : { rate: rateN }) });
      });
      const body = { ...form, issue, lines: out };
      if (!body.lines.length) throw new Error('Add at least one item with a quantity');
      if (!body.party_id) throw new Error('Choose the buyer');
      const created = await api.post('/api/delivery-orders', body);
      if (created.approval_required) {
        navigate('/approvals');
        return;
      }
      navigate('/delivery-orders');
    } catch (e) {
      setError(e.details ? `${e.message}: ${JSON.stringify(e.details)}` : e.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">New delivery order</h1>
          <div className="page-sub">
            Enter the goods once. The godown despatch and the tax invoice are built from this — batches are chosen
            at the godown, where the stock actually is.
          </div>
        </div>
      </div>

      <Alert>{error}</Alert>

      <Card title="Buyer and despatch">
        <div className="row">
          <Field label="Find buyer" hint="name or GSTIN">
            <input type="search" value={buyerSearch} placeholder="Search ledgers…"
              onChange={(e) => setBuyerSearch(e.target.value)} />
          </Field>
          <Field label="Buyer" hint="Tally ledger">
            <select value={form.party_id} onChange={(e) => {
              const p = buyerOptions.find((x) => x.id === e.target.value);
              setChosen(p || null);
              setForm({ ...form, party_id: e.target.value, place_of_supply: p ? (p.place_of_supply || p.state_code || '') : '' });
            }}>
              <option value="">{buyerOptions.length ? 'Select buyer…' : 'No buyer matches'}</option>
              {buyerOptions.map((p) => <option key={p.id} value={p.id}>{p.name}{p.gstin ? ` — ${p.gstin}` : ''}</option>)}
            </select>
          </Field>
          <Field label="Godown">
            <select value={form.godown_id} onChange={(e) => setForm({ ...form, godown_id: e.target.value })}>
              {godowns.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </Field>
          <Field label="Place of supply" hint="GST state code">
            <input value={form.place_of_supply} onChange={(e) => setForm({ ...form, place_of_supply: e.target.value })} placeholder="19" />
          </Field>
          <Field label="Vehicle"><input value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} placeholder="WB 23 A 4411" /></Field>
        </div>
        {party && (
          <div className="card-note">
            {party.billing_address} · {interstate ? 'Inter-state supply — IGST will apply' : 'Intra-state supply — CGST + SGST will apply'}
          </div>
        )}
        {credit && (
          <div className="inline" style={{ marginTop: 10, gap: 18 }}>
            <span className="card-note">Outstanding <b className="num" style={{ color: 'var(--ink)' }}>{inr(credit.outstanding, 0)}</b></span>
            {credit.credit_limit !== null && (
              <span className="card-note">Limit <b className="num" style={{ color: 'var(--ink)' }}>{inr(credit.credit_limit, 0)}</b></span>
            )}
            {credit.overdue > 0 && (
              <span className="card-note" style={{ color: 'var(--critical-text)' }}>
                Overdue <b className="num">{inr(credit.overdue, 0)}</b>
              </span>
            )}
            {credit.on_hold && <span className="pill bad">on credit hold</span>}
            {credit.credit_limit !== null && preview.taxable > 0 && (
              <span className="card-note">
                After this order {inr(credit.outstanding + preview.taxable + preview.gst, 0)}
                {credit.outstanding + preview.taxable + preview.gst > credit.credit_limit
                  ? ' — over the limit, an approval will be needed' : ''}
              </span>
            )}
          </div>
        )}
      </Card>

      <Card title="Goods"
        actions={<button className="btn small secondary" onClick={() => setLines([...lines, blankLine()])}>＋ Add line</button>}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Item</th><th style={{ width: 150 }}>Quantity</th><th style={{ width: 150 }}>Rate</th>
              <th className="r" style={{ width: 130 }}>Value</th><th style={{ width: 52 }} /></tr></thead>
            <tbody>
              {lines.map((l, idx) => {
                const item = items.find((i) => i.id === l.item_id);
                return (
                  <tr key={idx}>
                    <td>
                      <ItemPicker items={items} value={l.item_id} godownName={godownName}
                        onChange={(it) => setLine(idx, { item_id: it.id, rate: l.rate || it.default_rate || '' })} />
                      {item && (
                        <div className="sub">
                          Available <b className="num" style={{ color: figure(l.qty) > num(item.qty_available) ? 'var(--critical-text)' : 'var(--ink)' }}>
                            {qty(item.qty_available, item.uom)}</b>
                          {godownName ? ` (${qty(item.qty_godown, item.uom)} in ${godownName})` : ''}
                          {' '}· HSN {item.hsn || '—'} · GST {item.gst_rate}%
                        </div>
                      )}
                    </td>
                    <td><input className="num" value={l.qty} inputMode="decimal" onChange={(e) => setLine(idx, { qty: e.target.value })} /></td>
                    <td>
                      <input className="num" value={l.rate} inputMode="decimal"
                        style={belowFloor(l) ? { borderColor: 'var(--critical)' } : undefined}
                        onChange={(e) => setLine(idx, { rate: e.target.value })} />
                      {prices[l.item_id] && (
                        <div className="sub" style={belowFloor(l) ? { color: 'var(--critical-text)' } : undefined}>
                          list {prices[l.item_id].rate}
                          {prices[l.item_id].min_rate !== null ? ` · floor ${prices[l.item_id].min_rate}` : ' · no floor'}
                          {belowFloor(l) ? ' · needs approval' : ''}
                        </div>
                      )}
                    </td>
                    <td className="r num">{figure(l.qty) && figure(l.rate) ? inr(figure(l.qty) * figure(l.rate), 0) : '—'}</td>
                    <td className="r">
                      <button className="btn small ghost" aria-label="Remove line"
                        onClick={() => setLines(lines.length === 1 ? [blankLine()] : lines.filter((_, i) => i !== idx))}>✕</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="inline" style={{ justifyContent: 'flex-end', marginTop: 12, gap: 24 }}>
          <span className="card-note">Taxable <b className="num" style={{ color: 'var(--ink)' }}>{inr(preview.taxable, 0)}</b></span>
          <span className="card-note">GST <b className="num" style={{ color: 'var(--ink)' }}>{inr(preview.gst, 0)}</b></span>
          <span className="card-note">Estimated total <b className="num" style={{ color: 'var(--ink)' }}>{inr(preview.taxable + preview.gst, 0)}</b></span>
        </div>
      </Card>

      <div className="inline">
        <button className="btn big" disabled={busy} onClick={() => submit(true)}>Save &amp; issue</button>
        <button className="btn secondary" disabled={busy} onClick={() => submit(false)}>Save as draft</button>
        <span className="card-note">
          Issuing takes the next number in the series and sends it to the godown queue. A rate below its floor, or a
          buyer past their limit, goes for approval first — the order is saved either way.
        </span>
      </div>
    </>
  );
}
